import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine } from "recharts";
import { Gauge, Fuel, Wrench, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const nf = (n) => (n == null ? "-" : Number(n).toLocaleString("it-IT"));
const FUEL = { "1/4": "¼", "1/2": "½", pieno: "Pieno" };

function ServiceKm({ g, idx, onSaved }) {
  const [val, setVal] = useState(g.next_service_km ?? "");
  const save = async () => {
    try {
      await api.put(`/vehicles/${g.vehicle_id}/service`, { next_service_km: val === "" ? null : Number(val) });
      toast.success("Km del prossimo tagliando salvati");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };
  const warn = g.km_to_service != null && g.km_to_service < 1000;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Wrench className="h-4 w-4 text-muted-foreground" />
      <span>Prossimo tagliando a</span>
      <Input type="number" min="1" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`service-km-input-${idx}`} className="h-8 w-32 text-sm" />
      <span>km</span>
      <Button size="icon" variant="ghost" onClick={save} data-testid={`service-km-save-${idx}`} className="h-8 w-8"><Save className="h-4 w-4" /></Button>
      {g.km_to_service != null && (
        <span data-testid={`service-km-left-${idx}`} className={`font-semibold ${warn ? "text-red-700" : "text-emerald-700"}`}>
          {g.km_to_service > 0 ? `mancano ${nf(g.km_to_service)} km` : `superato di ${nf(-g.km_to_service)} km`}
        </span>
      )}
    </div>
  );
}

function VehicleKm({ g, idx, onSaved }) {
  const data = g.rows.map((r) => ({ ...r, label: new Date(r.signed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) }));
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid={`km-vehicle-${idx}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{g.vehicle_name}</p>
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1" data-testid={`km-last-${idx}`}><Gauge className="h-4 w-4" /> {nf(g.last_km)} km</span>
          <span className={`flex items-center gap-1 ${g.last_fuel === "1/4" ? "text-red-700 font-semibold" : ""}`} data-testid={`km-fuel-${idx}`}><Fuel className="h-4 w-4" /> {FUEL[g.last_fuel] || "-"}</span>
          <span className="text-muted-foreground" data-testid={`km-period-${idx}`}>{nf(g.km_period)} km nel periodo</span>
        </div>
      </div>
      <ServiceKm g={g} idx={idx} onSaved={onSaved} />
      {data.length > 0 && (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={["dataMin", g.next_service_km ? Math.max(g.next_service_km, ...data.map((d) => d.km)) : "dataMax"]} tick={{ fontSize: 11 }} tickFormatter={nf} width={70} />
            <Tooltip formatter={(v) => [`${nf(v)} km`, "Km"]} />
            {g.next_service_km && <ReferenceLine y={g.next_service_km} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "Tagliando", fontSize: 11, fill: "#DC2626", position: "insideTopLeft" }} />}
            <Line type="monotone" dataKey="km" stroke="#D32F2F" strokeWidth={2} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
      {data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-muted-foreground text-left"><th className="py-1.5 pr-3">Data</th><th className="pr-3">Km</th><th className="pr-3">Carburante</th><th className="pr-3">Autista</th><th>Servizio</th></tr></thead>
            <tbody>
              {[...data].reverse().map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`km-row-${r.id}`}>
                  <td className="py-1.5 pr-3">{new Date(r.signed_at).toLocaleString("it-IT")}</td>
                  <td className="pr-3 font-semibold tabular-nums">{nf(r.km)}</td>
                  <td className={`pr-3 ${r.carburante === "1/4" ? "text-red-700 font-semibold" : ""}`}>{FUEL[r.carburante] || "-"}</td>
                  <td className="pr-3">{r.compiled_by_name}</td>
                  <td>{r.service_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ChecklistKm() {
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState(() => ({ vehicle_id: "", date_from: iso(new Date(Date.now() - 90 * 864e5)), date_to: iso(new Date()) }));
  const [list, setList] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/checklists/km", { params }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [f, tick]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4" data-testid="km-view">
      <div className="grid sm:grid-cols-4 gap-3 rounded-xl border border-border bg-card p-4">
        <div className="space-y-1">
          <Label>Ambulanza</Label>
          <Select value={f.vehicle_id || "all"} onValueChange={(v) => set("vehicle_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="km-filter-vehicle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Dal</Label><Input type="date" data-testid="km-date-from" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} /></div>
        <div className="space-y-1"><Label>Al</Label><Input type="date" data-testid="km-date-to" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} /></div>
        <p className="text-xs text-muted-foreground self-end">Avviso ai coordinatori quando al tagliando mancano meno di 1.000 km.</p>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="km-empty">Nessuna rilevazione Km: compaiono dalle check-list Mezzo.</p>
      ) : list.map((g, i) => <VehicleKm key={g.vehicle_id} g={g} idx={i} onSaved={() => setTick((t) => t + 1)} />)}
    </div>
  );
}
